import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { isDesktop, isTablet } from 'react-device-detect'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import routePaths from './constants/routePaths'
import Home from './pages/Home'
import Welcome from './components/Welcome'
import MotifCounter from './components/MotifCounter'
import ExplanationFeature from './components/ExplanationFeature'
import TagFilter from './components/TagFilter'
import SideMenu from './components/SideMenu'
import MotifGenerator from './pages/MotifGenerator'
import WhiteOverlay from './components/WhiteOverlay'
import { isFilterElement, RoutableElement, useContextPanels } from './contexts/panelsContext'
import useOpenFilter from './components/FilterBar/useOpenFilter'
import { useContextAppStatus } from './contexts/appStatusContext'
import FullScreenLoading from './components/FullScreenLoading'
import LanguageChanger from './components/LanguageChanger'
import GeographicFilter from './components/GeographicFilter'
import KeywordsFilter from './components/KeywordsFilter'
import ErrorMessage from './components/ErrorMessage'
import Snackbar from './components/Snackbar'
import { useContextSelectedMotifs } from './contexts/selectedMotifContext'
import BrandLogo from './components/BrandLogo'
import { useEnv } from './utils/useEnv'
import RotateDeviceNotification from './components/RotateDeviceNotification'
import ScreensaverContext from './contexts/screensaverContext'
import Gallery from './components/Gallery'
import AboutProject from './components/AboutProject'
import InteractionMotivatorContext from './contexts/interactionMotivatorContext'
import { UIElement } from './types/UIElements'
import LegalNotice from './components/LegalNotice'
import MotifDetails from './components/MotifDetails'
import MotifDetailsQueryHandler from './components/MotifDetailsQueryHandler'
import MotifDetailsCardHandler from './components/MotifDetailsCardHandler'
import { useContextMotifDetails } from './contexts/motifDetailsContext'
import FilterBar from './components/FilterBar'

const Router = () => (
  <BrowserRouter>
    <AppRoutes />
  </BrowserRouter>
)

const AppRoutes = () => {
  const { isPanelOpen, openFromRoute } = useContextPanels()
  const { showFilter } = useOpenFilter()
  const { isInitialLoading, rotateDeviceNotificationVisible } = useContextAppStatus()
  const { isWeb, isKiosk } = useEnv()
  const { selectedMotifs } = useContextSelectedMotifs()
  const [showLoadingScreen, setShowLoadingScreen] = useState(false)
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)
  const hasLoadedOnceRef = useRef(false)
  const { areDetailsOpen } = useContextMotifDetails()
  const location = useLocation()
  const firstSegment = useMemo(
    () => decodeURIComponent(location.pathname.split('/')[1] || '').toLowerCase(),
    [location.pathname],
  )
  const urlLocation = firstSegment

  const getElementToOpen = useCallback((): RoutableElement | undefined => {
    const allowedRoutes: Record<string, RoutableElement> = {
      about: UIElement.ABOUT,
      gallery: UIElement.GALLERY,
      how_it_works: UIElement.EXPLANATION,
      explanation: UIElement.EXPLANATION,
      legalnotice: UIElement.LEGALNOTICE,
      theme: UIElement.TAGFILTER,
      tags: UIElement.KEYWORDFILTER,
      location: UIElement.GEOFILTER,
    }

    return allowedRoutes[urlLocation]
  }, [urlLocation])

  const shouldStopLoadingAnimation = useMemo(() => {
    const element = getElementToOpen()
    const isCardRoute = firstSegment === 'card'
    const isCardOldRoute = firstSegment === 'card_old'
    const isIdRoute = /^\d/.test(firstSegment)

    return Boolean(element) || isCardRoute || isCardOldRoute || isIdRoute
  }, [firstSegment, getElementToOpen])

  useEffect(() => {
    const element = getElementToOpen()
    if (!element) return
    if (isFilterElement(element)) showFilter(element)
    else openFromRoute(element)
    // only when the address changes
  }, [getElementToOpen])

  const handleAnimationComplete = useCallback((forceDismiss?: boolean) => {
    setIsAnimationComplete(true)

    if (forceDismiss) {
      hasLoadedOnceRef.current = true
      setShowLoadingScreen(false)
    }
  }, [])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    if (isInitialLoading && !showLoadingScreen && !hasLoadedOnceRef.current) {
      setShowLoadingScreen(true)
    }

    if (!isInitialLoading && showLoadingScreen && isAnimationComplete) {
      hasLoadedOnceRef.current = true
      timeoutId = setTimeout(() => {
        setShowLoadingScreen(false)
      }, 1000)
    }

    return () => timeoutId && clearTimeout(timeoutId)
  }, [isInitialLoading, showLoadingScreen, isAnimationComplete])

  const shouldShowWelcome = location.pathname === '/' || location.pathname === routePaths.GENERATE

  const isValidCardPath =
    /^\d/.test(firstSegment) || firstSegment === 'card' || firstSegment === 'card_old'

  const stopAnimationMinVisibleMs = useMemo(() => {
    const isCardRoute =
      firstSegment === 'card' || firstSegment === 'card_old' || /^\d/.test(firstSegment)
    return isCardRoute ? 7200 : 2000
  }, [firstSegment])

  return (
    <ScreensaverContext>
      {showLoadingScreen && (
        <FullScreenLoading
          isCompleted={!isInitialLoading}
          onAnimationComplete={handleAnimationComplete}
          stopAnimation={shouldStopLoadingAnimation}
          minVisibleMs={stopAnimationMinVisibleMs}
        />
      )}

      {/* the loading screen stays up for a moment after its animation completes */}
      <InteractionMotivatorContext canStart={isAnimationComplete && !showLoadingScreen}>
        <Routes>
          <Route path={routePaths.HOME} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route
            path={routePaths.GENERATE}
            element={
              selectedMotifs.length < 1 ? <Navigate to={routePaths.HOME} /> : <MotifGenerator />
            }
          />

          <Route path={routePaths.ABOUT} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route
            path={routePaths.GALLERY}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.EXPLANATION}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.LEGALNOTICE}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route path={routePaths.THEME} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route path={routePaths.TAGS} element={<Home canShowMotifMap={isAnimationComplete} />} />

          <Route
            path={routePaths.LOCATION}
            element={<Home canShowMotifMap={isAnimationComplete} />}
          />

          <Route
            path={routePaths.CARD_OLD}
            element={
              <>
                <Home canShowMotifMap={isAnimationComplete} />
                <MotifDetailsCardHandler />
              </>
            }
          />

          <Route
            path={routePaths.CARD}
            element={
              isValidCardPath ? (
                <>
                  <Home canShowMotifMap={isAnimationComplete} />
                  <MotifDetailsCardHandler />
                </>
              ) : (
                <Navigate to={routePaths.HOME} />
              )
            }
          />

          <Route path="*" element={<Navigate to={routePaths.HOME} />} />
        </Routes>

        <WhiteOverlay />
        <TagFilter />
        <KeywordsFilter />
        <GeographicFilter />
        {isPanelOpen(UIElement.GALLERY) && <Gallery />}
        {isPanelOpen(UIElement.ABOUT) && <AboutProject />}
        <LegalNotice />
        {areDetailsOpen && <MotifDetails />}
        <MotifDetailsQueryHandler />
        <MotifCounter />
        <FilterBar />
        <Snackbar />
        <ErrorMessage />
        {(isDesktop || isKiosk || isTablet) && <LanguageChanger />}
        {!isKiosk && isDesktop && isWeb && !isPanelOpen(UIElement.GEOFILTER) && <BrandLogo />}
        {rotateDeviceNotificationVisible && <RotateDeviceNotification />}
        {shouldShowWelcome && <Welcome />}
        <ExplanationFeature />
        <SideMenu />
      </InteractionMotivatorContext>
    </ScreensaverContext>
  )
}

export default Router
